import { useEffect, useMemo, useRef, useState } from "react";

import { Box, Container, Grid, Typography } from "@mui/material";
import i18next from "i18next";

import { MainLayout } from "@shared/ui/MainLayout";
import MDBox from "@vendor/components/MDBox";
import { PdfViewer } from "@shared/ui/PdfViewer";
import { useUserData } from "@shared/hooks/useUserData";
import { getUrlFile } from "@shared/utils/setting";

import { Cover } from "../components/Cover";
import { Referred } from "../components/Referred";
import { LinkUtils } from "../components/LinkUtils";
import { usePeopleCulture } from "../hooks/usePeopleCulture";
import { useManifestPdf } from "../hooks/useManifestPdf";
import { useMarkPeopleCultureViewed } from "../hooks/useMarkPeopleCultureViewed";

const COVER_FILE = "07_Cover_PP.jpg";
const MANIFEST_FILE = "01__PP_Manifiesto Cultura.pdf";

export const PeopleAndCulturePage = () => {
  const { data, isFetching } = usePeopleCulture();
  const { userSetting } = useUserData();
  const { mutate: markViewed } = useMarkPeopleCultureViewed();
  const notifiedRef = useRef(false);

  // Marca la pantalla como vista una sola vez, cuando el setting lo indica.
  useEffect(() => {
    if (userSetting?.viewPeopleCultureStart && !notifiedRef.current) {
      notifiedRef.current = true;
      markViewed();
    }
  }, [userSetting, markViewed]);

  // Imágenes disponibles: las del home más la portada.
  const imageServer = useMemo(() => {
    if (!data) return null;
    const cover = (data.imageCover ?? []).find((image) => image.fileName === COVER_FILE);
    return cover ? [...(data.imageHomePage ?? []), cover] : [...(data.imageHomePage ?? [])];
  }, [data]);

  const manifest = useMemo(
    () => imageServer?.find((file) => file.fileName === MANIFEST_FILE) ?? null,
    [imageServer]
  );

  const { data: manifestBlob } = useManifestPdf(manifest?.fileId, manifest?.extension);
  const [manifestSrc, setManifestSrc] = useState<string | null>(null);

  // Deriva un object URL del blob ya descargado y lo libera al desmontar.
  useEffect(() => {
    if (!manifestBlob) return undefined;
    const objectUrl = URL.createObjectURL(manifestBlob);
    setManifestSrc(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [manifestBlob]);

  const getFile = (fileName: string) => {
    const value = imageServer?.find((image) => image.fileName === fileName);
    if (!value) return "";
    return getUrlFile(value.fileId as string, value.extension as string);
  };

  return (
    <MainLayout loading={isFetching}>
      <Container>
        {imageServer && <Cover getFile={getFile} fileName={COVER_FILE} name="People & Culture" />}
        <MDBox py={3}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <Typography align="center" variant="h2">
                {i18next.t("cultureManifesto")}
              </Typography>
            </Grid>
            <Grid sx={{ marginTop: "20px" }} size={12}>
              <Box style={{ backgroundColor: "white" }}>
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <Container>
                      <Typography variant={"p" as "body1"} align="justify">
                        {i18next.t("peopleAndCultureMessage")}
                      </Typography>
                    </Container>
                  </Grid>
                  <Grid size={12}>
                    <PdfViewer
                      src={manifestSrc ?? undefined}
                      title={i18next.t("cultureManifesto")}
                      fileName="Manifiesto_de_Cultura.pdf"
                      downloadLabel={i18next.t("download")}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Grid container spacing={3}>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 12,
                          md: 12,
                          lg: 6,
                        }}
                      >
                        <Container>
                          <Box sx={{ padding: "50px" }}>
                            <Typography variant="h4" align="center">
                              {i18next.t("birthdayMonth")}
                            </Typography>
                            <Typography variant={"p" as "body1"} align="justify">
                              {i18next.t("birthdayMonthMessage")}
                            </Typography>
                            <img src={getFile("02_PP_cumple.png")} style={{ width: "100%" }} />
                          </Box>
                        </Container>
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 12,
                          md: 12,
                          lg: 6,
                        }}
                      >
                        <Container>
                          <Box sx={{ padding: "50px" }}>
                            <LinkUtils />
                          </Box>
                        </Container>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            <Referred getFile={getFile} />
          </Grid>
        </MDBox>
      </Container>
    </MainLayout>
  );
};
